import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown, Mail, Clock, ShieldCheck, Send } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { useAppConfig } from "@/hooks/useAppConfig";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FAQ = [
  {
    q: "Pour quels niveaux l'app est-elle prévue ?",
    a: "Chouette Apprentissage accompagne les élèves du CP à la Terminale. Votre enfant indique son âge : seules les classes proches de son niveau lui sont proposées, les autres restent verrouillées.",
  },
  {
    q: "L'app est-elle payante ?",
    a: "L'application propose un essai gratuit de 14 jours, puis un abonnement géré par le représentant légal : Solo (1 enfant) à 3,00 € par mois, ou Pack Famille (tous vos enfants, 2 et plus) à 5,00 € par mois. Le paiement se fait via l'App Store ou Google Play, avec renouvellement automatique, résiliable à tout moment depuis votre compte. Les clients Ptit Services bénéficient d'un accès gratuit.",
  },
  {
    q: "Le compagnon donne-t-il les réponses toutes faites ?",
    a: "Non. Le compagnon applique la méthode socratique : il ne donne jamais la réponse. Il pose les bonnes questions, repère les lacunes et guide l'élève jusqu'au « eurêka » — c'est comme ça qu'on comprend vraiment, et qu'on retient.",
  },
  {
    q: "Quelles matières sont couvertes ?",
    a: "Mathématiques, Français, Anglais, Espagnol, Allemand, Italien (dès la 5e), Histoire-Géographie, Physique-Chimie, SVT, Technologie, EMC et Philosophie (Terminale). Les chapitres proposés dépendent de la classe de l'élève.",
  },
  {
    q: "Comment fonctionne la messagerie entre enfants ?",
    a: "Les amis sont approuvés par un parent, chaque message est analysé par un gardien IA et les messages bloqués restent visibles par les parents. Les échanges privés entre co-parents sont protégés, avec accusé de lecture.",
  },
  {
    q: "Comment sont activées les fonctions sociales (amis, défis, messagerie) ?",
    a: "Elles ne s'activent qu'après confirmation de l'e-mail et vérification de l'identité d'un représentant légal (pièce d'identité + selfie). Jusqu'à deux représentants par enfant : le co-parent est invité par e-mail.",
  },
  {
    q: "Comment sont protégées les données de mon enfant ?",
    a: "Nous appliquons les principes du RGPD : données minimales, aucune publicité, aucun lien externe, aucune revente, et des données hébergées en France. Le détail est décrit dans notre politique de confidentialité.",
  },
  {
    q: "Que faire si je rencontre un problème technique ?",
    a: "Écrivez-nous via le formulaire ci-contre en décrivant le problème (et l'écran concerné) : nous revenons vers vous le plus vite possible.",
  },
];

const FaqItem = ({ item, index }) => {
  const [open, setOpen] = useState(index === 0);
  return (
    <div className="card-paper overflow-hidden" data-testid={`faq-item-${index}`}>
      <button
        className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        data-testid={`faq-toggle-${index}`}
      >
        <span className="font-bold text-sm sm:text-base">{item.q}</span>
        <ChevronDown className={`w-5 h-5 text-coral shrink-0 transition-transform duration-300 ${open ? "rotate-180" : ""}`} />
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: [0.22, 1, 0.36, 1] }}
          >
            <p className="px-5 pb-5 text-sm text-ink/65 leading-relaxed">{item.a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default function Support() {
  const config = useAppConfig();
  const [form, setForm] = useState({ name: "", email: "", subject: "Question sur l'app", message: "" });
  const [sending, setSending] = useState(false);

  const change = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Merci de remplir tous les champs obligatoires.");
      return;
    }
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/support`, form);
      toast.success(
        data.email_sent
          ? "Message envoyé ! Un accusé de réception vient d'arriver dans votre boîte e-mail."
          : "Message envoyé ! Nous revenons vers vous très vite."
      );
      setForm({ name: "", email: "", subject: "Question sur l'app", message: "" });
    } catch (err) {
      toast.error("L'envoi a échoué. Réessayez ou écrivez-nous directement par e-mail.");
    } finally {
      setSending(false);
    }
  };

  return (
    <main className="pt-28 pb-24">
      <div className="max-w-7xl mx-auto px-6">
        <div className="max-w-2xl">
          <Reveal><span className="eyebrow">Support</span></Reveal>
          <Reveal delay={0.05}>
            <h1 className="mt-3 font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight" data-testid="support-title">
              Besoin d'un <span className="italic text-coral">coup de patte</span> ?
            </h1>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-5 text-lg text-ink/65 leading-relaxed">
              Une question, un bug, une suggestion ? Écrivez-nous : une vraie personne vous répond.
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid lg:grid-cols-12 gap-10 items-start">
          <Reveal className="lg:col-span-7">
            <form onSubmit={submit} className="card-paper p-6 sm:p-8 space-y-5" data-testid="support-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <label htmlFor="name" className="text-sm font-bold">Votre nom *</label>
                  <input id="name" name="name" className="field mt-1.5" placeholder="Marie Dupont" value={form.name} onChange={change} data-testid="support-name-input" />
                </div>
                <div>
                  <label htmlFor="email" className="text-sm font-bold">Votre e-mail *</label>
                  <input id="email" name="email" type="email" className="field mt-1.5" placeholder="marie@exemple.fr" value={form.email} onChange={change} data-testid="support-email-input" />
                </div>
              </div>
              <div>
                <label htmlFor="subject" className="text-sm font-bold">Sujet</label>
                <select id="subject" name="subject" className="field mt-1.5" value={form.subject} onChange={change} data-testid="support-subject-select">
                  <option>Question sur l'app</option>
                  <option>Problème technique</option>
                  <option>Abonnement et facturation</option>
                  <option>Protection des données</option>
                  <option>Autre</option>
                </select>
              </div>
              <div>
                <label htmlFor="message" className="text-sm font-bold">Votre message *</label>
                <textarea id="message" name="message" rows={6} className="field mt-1.5 resize-none" placeholder="Décrivez votre demande…" value={form.message} onChange={change} data-testid="support-message-input" />
              </div>
              <button type="submit" disabled={sending} className="btn-coral w-full sm:w-auto" data-testid="support-submit-button">
                <Send className="w-4 h-4" />
                {sending ? "Envoi en cours…" : "Envoyer le message"}
              </button>
            </form>
          </Reveal>

          <div className="lg:col-span-5 space-y-5">
            <Reveal delay={0.1}>
              <div className="card-paper p-6 flex items-start gap-4" data-testid="support-delay-card">
                <Clock className="w-5 h-5 text-coral shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Temps de réponse</p>
                  <p className="text-sm text-ink/65 mt-1">Nous répondons généralement sous 48 h ouvrées.</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.15}>
              <a href={`mailto:${config.support_email}`} className="card-paper p-6 flex items-start gap-4 hover:-translate-y-0.5 transition-transform block" data-testid="support-email-card">
                <Mail className="w-5 h-5 text-coral shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Par e-mail</p>
                  <p className="text-sm text-ink/65 mt-1 break-all">{config.support_email}</p>
                </div>
              </a>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="card-paper p-6 flex items-start gap-4" data-testid="support-privacy-card">
                <ShieldCheck className="w-5 h-5 text-coral shrink-0 mt-0.5" />
                <div>
                  <p className="font-bold">Données & confidentialité</p>
                  <p className="text-sm text-ink/65 mt-1">
                    Vos messages servent uniquement à traiter votre demande. Détails dans notre{" "}
                    <a href="/confidentialite" className="underline text-coral font-semibold" data-testid="support-privacy-link">politique de confidentialité</a>.
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="mt-20 max-w-3xl">
          <Reveal><h2 className="font-serif text-3xl font-bold tracking-tight" data-testid="faq-title">Questions fréquentes</h2></Reveal>
          <div className="mt-7 space-y-3.5">
            {FAQ.map((item, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <FaqItem item={item} index={i} />
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </main>
  );
}